// Editor de páginas em blocos (TipTap): textos, listas, tarefas, toggles,
// callouts, tabelas, imagens, links para subpáginas e o bloco de transcrição
// de reunião, com o menu "/" e a barra de formatação ao selecionar texto.

import { Editor, Extension, Node, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Image from "@tiptap/extension-image";
import Highlight from "@tiptap/extension-highlight";
import { Details, DetailsSummary, DetailsContent } from "@tiptap/extension-details";
import { Placeholder } from "@tiptap/extensions";
import { TableKit } from "@tiptap/extension-table";
import Suggestion from "@tiptap/suggestion";
import Mention from "@tiptap/extension-mention";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import * as store from "./store.js";
import { uploadFile } from "./api.js";
import { el, icon, titleOf, toast, fmtDate, fmtDateTime, overlayHost, popup } from "./dom.js";
import { api } from "./api.js";
import { iconNode } from "./icons.js";
import { mountMeetingBlock } from "./meeting.js";
import { mountDatabase } from "./database.js";
import { avatar } from "./people.js";

// ---------- Blocos próprios ----------

const Callout = Node.create({
  name: "callout",
  group: "block",
  content: "block+",
  defining: true,
  addAttributes() {
    return { emoji: { default: "💡", parseHTML: (e) => e.getAttribute("data-emoji") || "💡" } };
  },
  parseHTML() {
    return [{ tag: "div[data-callout]" }, { tag: "aside" }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-callout": "", "data-emoji": node.attrs.emoji, class: "callout" }), 0];
  },
  addNodeView() {
    return ({ node, editor, getPos }) => {
      const dom = el("div", { class: "callout", "data-callout": "" });
      const emoji = el("button", { class: "callout-emoji", contenteditable: "false", title: "Trocar ícone" }, node.attrs.emoji);
      const contentDOM = el("div", { class: "callout-body" });
      emoji.addEventListener("mousedown", (e) => e.preventDefault());
      emoji.addEventListener("click", async () => {
        if (!editor.isEditable) return;
        const { emojiPicker } = await import("./dialogs.js");
        emojiPicker(emoji, (v) => {
          const pos = getPos();
          if (typeof pos === "number") editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, null, { emoji: v || "💡" }));
        });
      });
      dom.append(emoji, contentDOM);
      return {
        dom, contentDOM,
        update: (n) => {
          if (n.type.name !== "callout") return false;
          emoji.textContent = n.attrs.emoji;
          return true;
        },
      };
    };
  },
});

const PageLink = Node.create({
  name: "pageLink",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return { id: { default: null }, title: { default: "" } };
  },
  parseHTML() {
    return [{ tag: "div[data-page-link]", getAttrs: (e) => ({ id: e.getAttribute("data-id"), title: e.textContent }) }];
  },
  renderHTML({ node }) {
    return ["div", { "data-page-link": "", "data-id": node.attrs.id }, node.attrs.title || "Página"];
  },
  addNodeView() {
    return ({ node }) => {
      let id = node.attrs.id;
      const dom = el("div", { class: "page-link", "data-page-link": "", contenteditable: "false" });
      const draw = () => {
        const p = store.node(id);
        // Sem acesso (ou excluída): nunca mostra o título guardado no texto.
        dom.classList.toggle("locked", !p);
        dom.title = p ? "" : "Você não tem acesso a esta página, ou ela foi excluída";
        dom.replaceChildren(
          el("span", { class: "page-link-icon" }, p ? icon(p) : "🔒"),
          el("span", { class: "page-link-title" + (p ? "" : " missing") }, p ? titleOf(p) : "Sem acesso"));
      };
      draw();
      const off = store.on("tree", draw);
      dom.addEventListener("click", async () => {
        if (!id || !store.node(id)) return;
        (await import("./router.js")).openPage(id);
      });
      return {
        dom,
        update: (n) => {
          if (n.type.name !== "pageLink") return false;
          id = n.attrs.id;
          draw();
          return true;
        },
        destroy: off,
        ignoreMutation: () => true,
      };
    };
  },
});

// Banco de dados embutido na página (como as visões "inline" do Notion): o
// quadro aparece e funciona dentro do texto; o título abre o banco inteiro.
const DatabaseView = Node.create({
  name: "databaseView",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return { id: { default: null }, title: { default: "" } };
  },
  parseHTML() {
    return [{ tag: "div[data-database-view]", getAttrs: (e) => ({ id: e.getAttribute("data-id"), title: e.textContent }) }];
  },
  renderHTML({ node }) {
    return ["div", { "data-database-view": "", "data-id": node.attrs.id }, node.attrs.title || "Banco de dados"];
  },
  addNodeView() {
    return ({ node }) => {
      let id = node.attrs.id;
      let db = null;
      const head = el("div", { class: "dbv-head" });
      const body = el("div", { class: "dbv-body" });
      const dom = el("div", { class: "dbv", "data-database-view": "", contenteditable: "false" }, head, body);
      const drawHead = () => {
        const p = store.node(id);
        head.replaceChildren(el("button", { class: "dbv-title" + (p ? "" : " locked"), title: p ? "Abrir o banco inteiro" : "Você não tem acesso a este banco, ou ele foi excluído",
          onclick: async () => { if (p) (await import("./router.js")).openPage(id); } },
          el("span", { class: "dbv-icon" }, p ? icon(p) : "🔒"), el("span", {}, p ? titleOf(p) : "Sem acesso")));
      };
      const mount = () => {
        db?.destroy();
        db = null;
        body.replaceChildren();
        if (!id) return;
        const p = store.node(id);
        // Banco de um ambiente ou página sem acesso: nada dele aparece aqui.
        if (!p) {
          body.replaceChildren(el("p", { class: "dbv-locked muted" }, "Este banco está num lugar que você não pode ver."));
          return;
        }
        db = mountDatabase(body, id, {
          canEdit: p ? store.canEdit(p) : false,
          openRow: async (rowId, o = {}) => {
            if (o.full) return (await import("./router.js")).openPage(rowId);
            (await import("./page.js")).openPeek(rowId);
          },
          openSettings: (tab, propId) => import("./dbsettings.js").then((m) => m.openDbSettings(id, tab, propId)),
        });
      };
      drawHead();
      mount();
      const off = store.on("tree", drawHead);
      return {
        dom,
        update: (n) => {
          if (n.type.name !== "databaseView") return false;
          if (n.attrs.id !== id) { id = n.attrs.id; mount(); }
          drawHead();
          return true;
        },
        // O quadro cuida dos próprios cliques, digitação e arrastar.
        stopEvent: (e) => !(e.type === "dragstart" && e.target === dom) && body.contains(e.target),
        ignoreMutation: () => true,
        destroy: () => { off(); db?.destroy(); },
      };
    };
  },
});

function meetingNode(ctx) {
  return Node.create({
    name: "meeting",
    group: "block",
    atom: true,
    selectable: true,
    draggable: false,
    addAttributes() {
      return { meetingId: { default: null } };
    },
    parseHTML() {
      return [{ tag: "div[data-meeting]", getAttrs: (e) => ({ meetingId: e.getAttribute("data-meeting") || null }) }];
    },
    renderHTML({ node }) {
      return ["div", { "data-meeting": node.attrs.meetingId || "" }];
    },
    addNodeView() {
      return ({ node, editor, getPos }) => {
        const dom = el("div", { class: "meeting-block", contenteditable: "false" });
        let attrs = { ...node.attrs };
        const block = mountMeetingBlock(dom, {
          pageId: ctx.pageId,
          pageTitle: () => ctx.pageTitle?.() || "",
          editable: () => editor.isEditable,
          getMeetingId: () => attrs.meetingId,
          setMeetingId: (meetingId) => {
            const pos = getPos();
            if (typeof pos !== "number") return;
            attrs = { ...attrs, meetingId };
            editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, null, { meetingId }));
          },
        });
        return {
          dom,
          update: (n) => {
            if (n.type.name !== "meeting") return false;
            if (n.attrs.meetingId !== attrs.meetingId) {
              attrs = { ...n.attrs };
              block.refresh();
            }
            return true;
          },
          stopEvent: () => true,
          ignoreMutation: () => true,
          destroy: () => block.destroy(),
        };
      };
    },
  });
}

// ---------- Menu "/" ----------

function commands(ctx) {
  return [
    { group: "Blocos básicos", title: "Texto", hint: "Parágrafo simples", icon: "T", keys: "texto paragrafo text", run: (c) => c.setParagraph() },
    { group: "Blocos básicos", title: "Título 1", hint: "Título grande", icon: "H1", keys: "titulo h1 heading", run: (c) => c.setHeading({ level: 1 }) },
    { group: "Blocos básicos", title: "Título 2", hint: "Título médio", icon: "H2", keys: "titulo h2 heading", run: (c) => c.setHeading({ level: 2 }) },
    { group: "Blocos básicos", title: "Título 3", hint: "Título pequeno", icon: "H3", keys: "titulo h3 heading", run: (c) => c.setHeading({ level: 3 }) },
    { group: "Blocos básicos", title: "Lista com marcadores", hint: "Lista simples", icon: "•", keys: "lista bullet marcadores", run: (c) => c.toggleBulletList() },
    { group: "Blocos básicos", title: "Lista numerada", hint: "1. 2. 3.", icon: "1.", keys: "lista numerada ordered", run: (c) => c.toggleOrderedList() },
    { group: "Blocos básicos", title: "Lista de tarefas", hint: "Caixas de seleção", icon: "☑", keys: "tarefa todo checklist to-do", run: (c) => c.toggleTaskList() },
    { group: "Blocos básicos", title: "Toggle", hint: "Conteúdo que abre e fecha", icon: "▸", keys: "toggle details recolher alternar", run: (c) => c.setDetails() },
    { group: "Blocos básicos", title: "Citação", hint: "Trecho em destaque", icon: "❝", keys: "citacao quote", run: (c) => c.toggleBlockquote() },
    { group: "Blocos básicos", title: "Callout", hint: "Aviso com ícone", icon: "💡", keys: "callout aviso destaque nota", run: (c) => c.insertContent({ type: "callout", attrs: { emoji: "💡" }, content: [{ type: "paragraph" }] }) },
    { group: "Blocos básicos", title: "Divisor", hint: "Linha horizontal", icon: "—", keys: "divisor linha hr divider", run: (c) => c.setHorizontalRule() },
    { group: "Blocos básicos", title: "Código", hint: "Bloco de código", icon: "</>", keys: "codigo code", run: (c) => c.toggleCodeBlock() },
    { group: "Blocos básicos", title: "Tabela simples", hint: "Tabela 3 × 3 no texto", icon: "⊞", keys: "tabela table", run: (c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true }) },
    { group: "Mídia", title: "Imagem", hint: "Enviar do computador", icon: "🖼", keys: "imagem foto image upload arquivo", special: "image" },
    { group: "Mídia", title: "Imagem por link", hint: "Cole o endereço de uma imagem da internet", icon: "🔗", keys: "imagem link url externa internet image embed", special: "image-url" },
    { group: "Páginas", title: "Página", hint: "Subpágina dentro desta", icon: "📄", keys: "pagina subpagina page", special: "page" },
    { group: "Páginas", title: "Banco de dados embutido", hint: "Quadro ou tabela dentro desta página", icon: "▦", keys: "banco database board kanban tabela quadro inline embutido", special: "database-inline" },
    { group: "Páginas", title: "Banco de dados em página", hint: "Board, tabela e calendário numa subpágina", icon: "▦", keys: "banco database board kanban tabela quadro pagina", special: "database" },
    { group: "Páginas", title: "Visão de um banco existente", hint: "Mostra aqui um quadro que já existe", icon: "▤", keys: "visao banco existente linked database view quadro", special: "database-link" },
    { group: "Páginas", title: "Link para página", hint: "Aponta para uma página existente", icon: "↗", keys: "link pagina mencionar mention", special: "link" },
    { group: "Inserir", title: "Mencionar pessoa ou página", hint: "Avisa a pessoa mencionada", icon: "@", keys: "mencionar mention pessoa arroba", run: (c) => c.insertContent("@") },
    { group: "Inserir", title: "Data ou lembrete", hint: "Ex.: amanhã, 30/10 14h", icon: "📅", keys: "data lembrete reminder date hoje amanha", run: (c) => c.insertContent("@lembrar ") },
    { group: "IA", title: "Pedir à IA", hint: "Escreva o que a IA deve fazer", icon: "✨", keys: "ia ai claude escrever gerar pedir", special: "ai" },
    { group: "IA", title: "Continuar escrevendo", hint: "A IA continua o texto", icon: "✨", keys: "ia ai continuar escrever", special: "ai:continue" },
    { group: "IA", title: "Resumir a página", hint: "Resumo em tópicos", icon: "✨", keys: "ia ai resumo resumir", special: "ai:summarize" },
    { group: "IA", title: "Encontrar tarefas", hint: "Lista as tarefas e próximos passos", icon: "✨", keys: "ia ai tarefas acoes action items", special: "ai:action_items" },
    { group: "IA", title: "Ideias", hint: "Brainstorm sobre um tema", icon: "✨", keys: "ia ai ideias brainstorm", special: "ai:brainstorm" },
    ctx.meetings !== false && store.canTranscribe() && { group: "Ordo", title: "Transcrição de reunião", hint: "Grava, transcreve e resume a chamada", icon: "🎙", keys: "transcricao reuniao meeting chamada gravar audio ata notas", run: (c) => c.insertContent([{ type: "meeting", attrs: { meetingId: null } }, { type: "paragraph" }]) },
  ].filter(Boolean);
}

const normalize = (s) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Lista flutuante das sugestões ("/" e "@"), navegável pelo teclado.
function listPopup(drawItem, emptyText) {
  let menu = null;
  let items = [];
  let index = 0;
  let pick = null;
  const draw = () => {
    let lastGroup = null;
    const nodes = [];
    items.forEach((it, i) => {
      if (it.group && it.group !== lastGroup) {
        lastGroup = it.group;
        nodes.push(el("div", { class: "slash-group" }, it.group));
      }
      nodes.push(el("button", {
        class: "slash-item" + (i === index ? " active" : ""),
        onmousedown: (e) => { e.preventDefault(); pick(it); },
        onmousemove: () => { if (index !== i) { index = i; draw(); } },
      }, ...drawItem(it)));
    });
    menu.replaceChildren(...(nodes.length ? nodes : [el("div", { class: "slash-empty" }, emptyText)]));
    menu.querySelector(".active")?.scrollIntoView({ block: "nearest" });
  };
  const place = (rect) => {
    if (!rect || !menu) return;
    const h = menu.offsetHeight || 320;
    const top = rect.bottom + 6 + h > innerHeight ? Math.max(8, rect.top - h - 6) : rect.bottom + 6;
    menu.style.left = `${Math.round(Math.min(rect.left, innerWidth - 300))}px`;
    menu.style.top = `${Math.round(top)}px`;
  };
  const take = (props) => {
    items = props.items;
    index = 0;
    pick = (it) => props.command(it);
    draw();
    place(props.clientRect?.());
  };
  return {
    onStart: (props) => {
      menu = el("div", { class: "slash-menu" });
      overlayHost().append(menu);
      take(props);
    },
    onUpdate: take,
    onKeyDown: ({ event }) => {
      if (!menu) return false;
      if (event.key === "ArrowDown") { index = (index + 1) % Math.max(items.length, 1); draw(); return true; }
      if (event.key === "ArrowUp") { index = (index - 1 + items.length) % Math.max(items.length, 1); draw(); return true; }
      if (event.key === "Enter" || event.key === "Tab") { if (items[index]) { pick(items[index]); return true; } return false; }
      if (event.key === "Escape") { menu.remove(); menu = null; return true; }
      return false;
    },
    onExit: () => {
      menu?.remove();
      menu = null;
    },
  };
}

// ---------- Menções "@": pessoas, páginas, datas e lembretes ----------

const pad2 = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// "amanhã", "sexta", "30/10", "30/10/2026 14h", "lembrar amanhã 9h"…
export function parseWhen(q) {
  const t = normalize(q).trim();
  const remind = /^(lembr|alert|avis)/.test(t);
  const rest = t.replace(/^(lembrar|lembrete|lembre|lembra|alertar|avisar)( me| de)?\s*/, "");
  const now = new Date();
  let day = null;
  const m = /(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/.exec(rest);
  if (/\bhoje\b/.test(rest)) day = new Date(now);
  else if (/depois de amanha/.test(rest)) day = new Date(now.getTime() + 2 * 86400_000);
  else if (/\bamanha\b/.test(rest)) day = new Date(now.getTime() + 86400_000);
  else if (m) {
    const y = m[3] ? (m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])) : now.getFullYear();
    day = new Date(y, Number(m[2]) - 1, Number(m[1]));
  } else {
    const names = ["domingo", "segunda", "terca", "quarta", "quinta", "sexta", "sabado"];
    const i = names.findIndex((n) => rest.startsWith(n.slice(0, 3)) && rest.length >= 3);
    if (i >= 0) {
      day = new Date(now);
      day.setDate(now.getDate() + (((i - now.getDay()) + 7) % 7 || 7));
    }
  }
  if (!day && !remind) return [];
  if (!day) day = new Date(now.getTime() + 86400_000);
  const h = /(\d{1,2})(?:h|:)(\d{2})?/.exec(rest.replace(m?.[0] || "", ""));
  const out = [];
  const label = fmtDate(isoDay(day));
  if (!remind) out.push({ kind: "date", id: isoDay(day), label, title: label, hint: "Data" });
  const at = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h ? Number(h[1]) : 9, h?.[2] ? Number(h[2]) : 0);
  if (at.getTime() > Date.now()) out.push({ kind: "reminder", id: String(at.getTime()), label: fmtDateTime(at.getTime()), title: `Lembrete: ${fmtDateTime(at.getTime())}`, hint: "Você recebe um aviso na Caixa de entrada" });
  return out;
}

function mentionItems(query, ctx) {
  const q = normalize(query || "");
  const out = [];
  const users = store.state.users || [];
  for (const u of users.filter((x) => !q || normalize(x.name).includes(q) || normalize(x.email || "").startsWith(q)).slice(0, 6)) {
    out.push({ group: "Pessoas", kind: "user", id: u.id, label: u.name, title: u.name, hint: u.email || "" });
  }
  const pages = [...store.state.tree.values()].filter((p) => p.id !== ctx.pageId && (!q || normalize(p.title).includes(q)))
    .sort((a, b) => b.updated_at - a.updated_at).slice(0, q ? 6 : 4);
  for (const p of pages) out.push({ group: "Páginas", kind: "page", id: p.id, label: titleOf(p), title: titleOf(p), hint: "", page: p });
  const dates = q ? parseWhen(query) : [
    { kind: "date", id: isoDay(new Date()), label: fmtDate(isoDay(new Date())), title: "Hoje", hint: fmtDate(isoDay(new Date())) },
    { kind: "date", id: isoDay(new Date(Date.now() + 86400_000)), label: fmtDate(isoDay(new Date(Date.now() + 86400_000))), title: "Amanhã", hint: fmtDate(isoDay(new Date(Date.now() + 86400_000))) },
    ...parseWhen("lembrar amanhã 9h"),
  ];
  for (const d of dates) out.push({ group: "Datas e lembretes", ...d });
  return out;
}

const OrdoMention = Mention.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      kind: { default: "user", parseHTML: (e) => e.getAttribute("data-kind") || "user", renderHTML: (a) => ({ "data-kind": a.kind }) },
    };
  },
});

function mentionExtension(ctx) {
  return OrdoMention.configure({
    HTMLAttributes: { class: "mention" },
    renderText: ({ node }) => (node.attrs.kind === "user" ? "@" : "") + (node.attrs.label || ""),
    renderHTML: ({ options, node }) => {
      const kind = node.attrs.kind || "user";
      if (kind === "page") {
        // Página sem acesso (ou excluída): sem o título guardado, como no Notion.
        const p = store.node(node.attrs.id);
        return ["span", { ...options.HTMLAttributes, class: "mention mention-page" + (p ? "" : " locked"), "data-type": "mention", "data-id": node.attrs.id,
          "data-label": node.attrs.label, "data-kind": "page", title: p ? "" : "Você não tem acesso a esta página" }, p ? `↗ ${titleOf(p)}` : "🔒 Sem acesso"];
      }
      return ["span",
        { ...options.HTMLAttributes, class: `mention mention-${kind}`, "data-type": "mention", "data-id": node.attrs.id, "data-label": node.attrs.label, "data-kind": kind },
        (kind === "user" ? "@" : kind === "reminder" ? "⏰ " : kind === "date" ? "📅 " : "↗ ") + (node.attrs.label || "")];
    },
    suggestion: {
      char: "@",
      items: ({ query }) => (ctx.pageId ? mentionItems(query, ctx) : []),
      command: ({ editor, range, props }) => {
        editor.chain().focus().insertContentAt(range, [
          { type: "mention", attrs: { id: props.id, label: props.label, kind: props.kind } }, { type: "text", text: " " }]).run();
        if (props.kind === "reminder") {
          api("POST", "/reminders", { page_id: ctx.pageId, at: Number(props.id), text: ctx.pageTitle?.() || "" })
            .then(() => toast(`Lembrete marcado para ${props.label}.`))
            .catch((err) => toast("Não foi possível marcar o lembrete: " + err.message));
        }
      },
      render: () => listPopup((it) => [
        el("span", { class: "slash-icon" }, it.kind === "user" ? avatar({ id: it.id, name: it.label }, "sm")
          : it.kind === "page" ? iconNode(it.page?.icon, it.page?.kind === "database" ? "▦" : "📄") : it.kind === "reminder" ? "⏰" : "📅"),
        el("span", { class: "slash-text" }, el("strong", {}, it.title), it.hint ? el("span", {}, it.hint) : null)], "Ninguém encontrado"),
    },
  });
}

function slashMenu(ctx) {
  const list = commands(ctx);
  return Extension.create({
    name: "slashMenu",
    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          char: "/",
          allowSpaces: false,
          startOfLine: false,
          allow: ({ state, range }) => {
            const $from = state.doc.resolve(range.from);
            return !$from.parent.type.spec.code;
          },
          items: ({ query }) => {
            const q = normalize(query);
            return list.filter((c) => !q || normalize(c.title + " " + c.keys).includes(q)).slice(0, 20);
          },
          command: ({ editor, range, props }) => {
            const chain = editor.chain().focus().deleteRange(range);
            if (props.special) {
              chain.run();
              special(editor, props.special, ctx);
            } else {
              props.run(chain).run();
            }
          },
          render: () => listPopup((it) => [el("span", { class: "slash-icon" }, it.icon), el("span", { class: "slash-text" }, el("strong", {}, it.title), el("span", {}, it.hint))], "Nenhum bloco encontrado"),
        }),
      ];
    },
  });
}

async function special(editor, kind, ctx) {
  if (kind === "image") return pickImage(editor);
  if (kind === "image-url") return imageByUrl(editor);
  if (kind === "database-inline") {
    const id = ctx.createChild("database");
    editor.chain().focus().insertContent([{ type: "databaseView", attrs: { id } }, { type: "paragraph" }]).run();
    ctx.flush?.();
    return;
  }
  if (kind === "database-link") {
    const { pagePicker } = await import("./dialogs.js");
    pagePicker((id) => editor.chain().focus().insertContent([{ type: "databaseView", attrs: { id } }, { type: "paragraph" }]).run(), ctx.pageId, { kind: "database" });
    return;
  }
  if (kind.startsWith("ai")) {
    const { aiAtCursor } = await import("./ai-ui.js");
    return aiAtCursor(editor, ctx, kind.split(":")[1] || "write");
  }
  if (kind === "page" || kind === "database") {
    const id = ctx.createChild(kind);
    editor.chain().focus().insertContent([{ type: "pageLink", attrs: { id } }, { type: "paragraph" }]).run();
    // Salva em segundo plano e já abre a nova página.
    ctx.flush?.();
    ctx.openPage(id);
    return;
  }
  if (kind === "link") {
    const { pagePicker } = await import("./dialogs.js");
    pagePicker((id) => editor.chain().focus().insertContent([{ type: "pageLink", attrs: { id } }, { type: "paragraph" }]).run(), ctx.pageId);
  }
}

const IMG_URL = /^https?:\/\/\S+\.(png|jpe?g|gif|webp|svg|avif|bmp)(\?\S*)?$/i;

function imageByUrl(editor) {
  const input = el("input", { type: "url", placeholder: "https://…/imagem.png", class: "pop-input" });
  const ok = () => {
    const url = input.value.trim();
    if (!/^https?:\/\//i.test(url)) return input.focus();
    editor.chain().focus().insertContent([{ type: "image", attrs: { src: url, alt: "" } }, { type: "paragraph" }]).run();
    box.close();
  };
  input.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); ok(); } });
  const { from } = editor.state.selection;
  const at = editor.view.coordsAtPos(from);
  const box = popup({ x: at.left, y: at.bottom }, el("div", { class: "pop-edit" },
    el("strong", {}, "Imagem por link"), input,
    el("div", { class: "pop-hint" }, "Funciona com qualquer imagem pública (PNG, JPG, GIF, SVG, WebP)."),
    el("div", { class: "pop-row" }, el("span"), el("button", { class: "primary small", onclick: ok }, "Inserir"))));
  requestAnimationFrame(() => input.focus());
}

function pickImage(editor) {
  const input = el("input", { type: "file", accept: "image/*" });
  input.addEventListener("change", () => {
    const file = input.files?.[0];
    if (file) uploadInto(editor, [file], editor.state.selection.from);
  });
  input.click();
}

async function uploadInto(editor, files, pos) {
  for (const file of files) {
    toast(`Enviando ${file.name || "arquivo"}…`, 2500);
    try {
      const { url } = await uploadFile(file, file.name || (file.type.startsWith("image/") ? "imagem.png" : "arquivo"));
      const node = file.type.startsWith("image/")
        ? { type: "image", attrs: { src: url, alt: file.name || "" } }
        : { type: "paragraph", content: [{ type: "text", text: `📎 ${file.name || "arquivo"}`, marks: [{ type: "link", attrs: { href: url } }] }] };
      editor.chain().focus().insertContentAt(Math.min(pos, editor.state.doc.content.size), node).run();
    } catch (err) {
      toast("Não foi possível enviar: " + err.message);
    }
  }
}

// ---------- Barra de formatação ----------

function formatBar(editor) {
  const bar = el("div", { class: "format-bar", hidden: true });
  const btn = (label, title, run, isActive) => {
    const b = el("button", { title, onmousedown: (e) => { e.preventDefault(); run(); update(); } }, label);
    b.isActive = isActive;
    return b;
  };
  const turnInto = el("select", { class: "turn-into", title: "Transformar em", onmousedown: (e) => e.stopPropagation(), onchange: (e) => {
    const c = editor.chain().focus();
    const v = e.target.value;
    if (v === "p") c.setParagraph().run();
    else if (v === "todo") c.toggleTaskList().run();
    else if (v === "ul") c.toggleBulletList().run();
    else if (v === "ol") c.toggleOrderedList().run();
    else if (v === "quote") c.toggleBlockquote().run();
    else c.setHeading({ level: Number(v) }).run();
  } }, [["p", "Texto"], ["1", "Título 1"], ["2", "Título 2"], ["3", "Título 3"], ["ul", "Lista"], ["ol", "Numerada"], ["todo", "Tarefas"], ["quote", "Citação"]].map(([v, l]) => el("option", { value: v }, l)));
  const buttons = [
    btn("B", "Negrito (Ctrl+B)", () => editor.chain().focus().toggleBold().run(), () => editor.isActive("bold")),
    btn("I", "Itálico (Ctrl+I)", () => editor.chain().focus().toggleItalic().run(), () => editor.isActive("italic")),
    btn("U", "Sublinhado (Ctrl+U)", () => editor.chain().focus().toggleUnderline().run(), () => editor.isActive("underline")),
    btn("S", "Riscado", () => editor.chain().focus().toggleStrike().run(), () => editor.isActive("strike")),
    btn("</>", "Código", () => editor.chain().focus().toggleCode().run(), () => editor.isActive("code")),
    btn("🖍", "Marca-texto", () => editor.chain().focus().toggleHighlight().run(), () => editor.isActive("highlight")),
    btn("🔗", "Link", () => {
      const prev = editor.getAttributes("link").href || "";
      linkPrompt(bar, prev, (href) => {
        if (href === null) return;
        if (!href) editor.chain().focus().extendMarkRange("link").unsetLink().run();
        else editor.chain().focus().extendMarkRange("link").setLink({ href: /^[a-z]+:/i.test(href) ? href : "https://" + href }).run();
      });
    }, () => editor.isActive("link")),
  ];
  const aiBtn = el("button", { class: "fb-ai", title: "Pedir à IA (melhorar, corrigir, traduzir…)", onmousedown: async (e) => {
    e.preventDefault();
    const { aiSelectionMenu } = await import("./ai-ui.js");
    aiSelectionMenu(editor, aiBtn);
  } }, "✨ IA");
  bar.append(aiBtn, turnInto, ...buttons);
  document.body.append(bar);
  const update = () => {
    const { state, view } = editor;
    const { from, to, empty } = state.selection;
    if (empty || !editor.isEditable || !view.hasFocus() || state.selection.node) {
      bar.hidden = true;
      return;
    }
    for (const b of buttons) b.classList.toggle("on", b.isActive());
    const parent = state.selection.$from.parent;
    turnInto.value = parent.type.name === "heading" ? String(parent.attrs.level) : "p";
    const a = view.coordsAtPos(from);
    const b = view.coordsAtPos(to);
    bar.hidden = false;
    const w = bar.offsetWidth;
    const left = Math.max(8, Math.min(innerWidth - w - 8, (a.left + b.left) / 2 - w / 2));
    const top = a.top - bar.offsetHeight - 8 < 8 ? b.bottom + 8 : a.top - bar.offsetHeight - 8;
    bar.style.left = left + "px";
    bar.style.top = top + "px";
  };
  editor.on("selectionUpdate", update);
  editor.on("blur", () => setTimeout(() => { if (!bar.contains(document.activeElement)) bar.hidden = true; }, 100));
  return bar;
}

function linkPrompt(anchor, value, done) {
  import("./dom.js").then(({ popup }) => {
    const input = el("input", { class: "pop-input", value, placeholder: "Cole o link e tecle Enter" });
    let answered = false;
    const box = popup(anchor, el("div", { class: "pop-edit" }, input), { onClose: () => { if (!answered) done(null); } });
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { answered = true; done(input.value.trim()); box.close(); }
    });
    requestAnimationFrame(() => input.focus());
  });
}

// ---------- Criação ----------

// Extensões que definem o formato do documento (também usadas na importação).
// Com `collab`, o desfazer/refazer fica com o Yjs (só desfaz o que você fez).
export function baseExtensions(ctx = {}, { collab = false } = {}) {
  return [
    StarterKit.configure({
      ...(collab ? { undoRedo: false } : {}),
      link: { openOnClick: false, autolink: true, defaultProtocol: "https", protocols: ["ordo"] },
      dropcursor: { color: "#2383e2", width: 3 },
    }),
    TaskList,
    TaskItem.configure({ nested: true }),
    TableKit.configure({ table: { resizable: false } }),
    Image.configure({ inline: false, allowBase64: false }),
    Highlight,
    Details.configure({ persist: true, HTMLAttributes: { class: "toggle" } }),
    DetailsSummary,
    DetailsContent,
    Callout,
    PageLink,
    DatabaseView,
    meetingNode(ctx),
    mentionExtension(ctx),
  ];
}

// Cursor das outras pessoas: uma barra na cor delas com o nome em cima.
function caret(user) {
  const bar = document.createElement("span");
  bar.className = "collab-caret";
  bar.style.borderColor = user.color;
  const label = document.createElement("span");
  label.className = "collab-caret-label";
  label.style.background = user.color;
  label.textContent = user.name || "Alguém";
  bar.append(label);
  return bar;
}

// ctx: {pageId, pageTitle(), openPage(id), createChild(kind) -> id, flush()}
// live: sessão de edição simultânea (live.js); o texto vem do documento Yjs
// e é salvo pelo servidor, então `content` e `onChange` não são usados.
export function createEditor(element, { content, editable, onChange, ctx, placeholder, live }) {
  let timer = null;
  const collab = live ? [
    Collaboration.configure({ document: live.doc }),
    CollaborationCaret.configure({
      provider: live.provider,
      user: live.user,
      render: caret,
      selectionRender: (user) => ({ nodeName: "span", class: "collab-selection", style: `background-color: ${user.color}33` }),
    }),
  ] : [];
  const editor = new Editor({
    element,
    editable,
    ...(live ? {} : { content: content || "" }),
    extensions: [
      ...baseExtensions(ctx, { collab: !!live }),
      ...collab,
      Placeholder.configure({
        includeChildren: true,
        placeholder: ({ node }) => {
          if (node.type.name === "heading") return `Título ${node.attrs.level}`;
          if (node.type.name === "detailsSummary") return "Toggle";
          return placeholder || "Escreva algo ou digite \"/\" para inserir um bloco";
        },
      }),
      slashMenu(ctx),
    ],
    editorProps: {
      // Links: Ctrl+clique (ou clique, fora da edição) abre; links internos abrem a página.
      handleClick: (view, _pos, event) => {
        const m = event.target.closest?.(".mention");
        if (m && m.dataset.kind === "page" && m.dataset.id) {
          event.preventDefault();
          if (!store.node(m.dataset.id)) { toast("Você não tem acesso a esta página."); return true; }
          ctx.openPage?.(m.dataset.id);
          return true;
        }
        const a = event.target.closest?.("a[href]");
        if (!a) return false;
        if (view.editable && !(event.ctrlKey || event.metaKey)) return false;
        event.preventDefault();
        const href = a.getAttribute("href");
        const internal = /^ordo:(?:page:|\/\/page\/)([0-9a-f-]{36})$/i.exec(href);
        if (internal) ctx.openPage?.(internal[1]);
        else window.open(href, "_blank");
        return true;
      },
      handlePaste: (view, event) => {
        const files = [...(event.clipboardData?.files || [])];
        const text = event.clipboardData?.getData("text/plain")?.trim() || "";
        // Colar o endereço de uma imagem insere a imagem (como no Notion).
        // Colar o link de uma página do Ordo ("Copiar link") insere o link para ela.
        const pageLink = !files.length && editor.isEditable && /^ordo:(?:page:|\/\/page\/)([0-9a-f-]{36})$/i.exec(text);
        if (pageLink) {
          editor.chain().focus().insertContent([{ type: "pageLink", attrs: { id: pageLink[1] } }, { type: "paragraph" }]).run();
          return true;
        }
        if (!files.length && editor.isEditable && IMG_URL.test(text)) {
          editor.chain().focus().insertContent([{ type: "image", attrs: { src: text, alt: "" } }, { type: "paragraph" }]).run();
          return true;
        }
        if (!files.length || !editor.isEditable) return false;
        uploadInto(editor, files, view.state.selection.from);
        return true;
      },
      handleDrop: (view, event, _slice, moved) => {
        const files = [...(event.dataTransfer?.files || [])];
        if (moved || !files.length || !editor.isEditable) return false;
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ?? view.state.selection.from;
        uploadInto(editor, files, pos);
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      if (live) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        onChange?.(ed.getJSON());
      }, 600);
      editor.dirty = true;
    },
  });
  editor.ordoCtx = ctx || {};
  const bar = formatBar(editor);
  editor.dirty = false;
  editor.flush = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
      return onChange?.(editor.getJSON());
    }
    return undefined;
  };
  editor.pending = () => timer !== null;
  const destroy = editor.destroy.bind(editor);
  editor.destroy = () => {
    bar.remove();
    destroy();
  };
  return editor;
}
